<template>
  <div>
    <v-snackbar v-model="snackbar" :top="true" :color="color" :timeout="6000">
      <span v-if="snackbar_msg != null">{{ snackbar_msg }}</span>
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
    <v-card class="main-card">
      <v-toolbar class="custom-toolbar ma-2" flat dense>
        <v-toolbar-title>Social Information</v-toolbar-title>
      </v-toolbar>
      <v-row class="px-5">
        <v-col cols="12" sm="6" md="4" v-for="field in sociallinks" :key="field.field_name" v-if="field.isactive">
          <!-- Label -->
          <div v-if="field.input_type !== 'checkbox'" class="pl-1 pb-2" style="font-size: 14px;">
            <b>{{ field.label }}<span v-if="field.required" class="pl-1" style="color:red">*</span></b>
          </div>

          <!-- Text -->
          <v-text-field v-if="field.input_type === 'text'" v-model.trim="socialdetails[field.field_name]"
            :readonly="!field.iseditable" :filled="!field.iseditable" dense outlined rounded
            :placeholder="field.placeholder" :rules="[v => !v || /^(https?:\/\/)?[\w.-]+\.[a-z]{2,6}(\/[\w.-?=&#+\/_]*)*\/?$/i.test(v) || `${field.label} is not a valid URL`]"
            />
        </v-col>

        <v-col cols="12" sm="12" md="12" align="center" class="pb-5">
          <v-btn text class="button-77" @click="saveSocialDetails" :loading="save_loader">Save</v-btn>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  import axios from "axios";
  export default {
    props: {
      learner: {
        type: [String, Number],
        required: true,
      },
    },
    data() {
      return {
        color: "",
        snackbar: false,
        snackbar_msg: "",
        socialdetails: {
          webpage_link: "",
          linkedIn_link: "",
          facebook_link: "",
          instagram_link: "",
          scholar_link: "",
        },
        sociallinks: [],
        social_details_list: [],
        save_loader: false,
        editedIndex: -1,
      };
    },
    watch: {
      learner(newVal) {
        if (newVal) {
          this.getSocialDetails();
        }
      },
    },
    methods: {
      getSocialDetails() {
        const data = {
          learner: this.learner,
        };
        axios
          .post("/parent/getSocialDetails", data)
          .then((res) => {
            if (res.data.flag === "YES") {
              this.socialdetails = { ...this.socialdetails, ...res.data.socialdetails };
              this.sociallinks = res.data.sociallinks;
            } else {
              this.showSnackbar("error", res.data.snackbar);
            }
          })
          .catch(() => {
            this.showSnackbar("error", "Failed to fetch social details");
          });
      },
      saveSocialDetails() {
        const domainRules = {
          facebook_link: "facebook.com",
          webpage_link: "", // Allow any valid non-social URL
          linkedIn_link: "linkedin.com",
          instagram_link: "instagram.com",
          scholar_link: "scholar.google.com",
        };

        const values = [];
        const payload = { learner: this.learner };

        // Validate all active fields
        for (const field of this.sociallinks) {
          if (!field.isactive || field.input_type !== "text") continue;

          const value = (this.socialdetails[field.field_name] || "").trim();

          // Check required fields
          if (field.required && !value) {
            this.showSnackbar("error", `${field.label} is required`);
            return;
          }

          // Reject links with internal spaces
          if (value && /\s/.test(value)) {
            this.showSnackbar("error", `Invalid link in '${field.label}' — remove spaces`);
            return;
          }

          // Validate domain if specified
          if (value && domainRules[field.field_name]) {
            if (!value.includes(domainRules[field.field_name])) {
              this.showSnackbar("error", `Invalid ${field.label} link. Must include '${domainRules[field.field_name]}'`);
              return;
            }
          }

          // Validate URL format
          if (value) {
            const urlRegex =/^(https?:\/\/)?[\w.-]+\.[a-z]{2,6}(\/[\w.-?=&#+\/_]*)*\/?$/i;
            if (!urlRegex.test(value)) {
              this.showSnackbar("error", `${field.label} is not a valid URL`);
              return;
            }
            values.push(value);
            payload[field.field_name] = value;
          }
        }

        // Check for duplicate links
        const uniqueLinks = new Set(values);
        if (uniqueLinks.size !== values.length) {
          this.showSnackbar("error", "Duplicate links are not allowed");
          return;
        }

        // Prevent saving if no valid links are provided
        if (Object.keys(payload).length === 1) {
          this.showSnackbar("error", "No valid links to save");
          return;
        }

        this.save_loader = true;
        axios
          .post("/parent/saveSocialDetails", payload)
          .then((res) => {
            if (res.data.flag === "YES") {
              this.showSnackbar("success", res.data.snackbar);
              this.getSocialDetails(); // Refresh data after save
            } else {
              this.showSnackbar("error", res.data.snackbar);
            }
          })
          .catch(() => {
            this.showSnackbar("error", "Something went wrong");
          })
          .finally(() => {
            this.save_loader = false;
          });
      },
      showSnackbar(clr, msg) {
        this.snackbar = true;
        this.color = clr;
        this.snackbar_msg = msg;
      },
    },
    mounted() {
      this.getSocialDetails();
    },
  };
</script>

<style scoped>
  .button-77 {
    color: #fff;
    cursor: pointer;
    font-weight: 800;
    vertical-align: middle;
    z-index: 0;
  }

  .button-77:before,
  .button-77:after {
    border-radius: 80px;
  }

  .button-77:before {
    background-color: rgba(249, 58, 19, 0.32);
    content: "";
  }

  .button-77:after {
    background-image: linear-gradient(92.83deg, #ff7426 0, #f93a13 100%);
    bottom: 4px;
    content: "";
    display: block;
    left: 4px;
    overflow: hidden;
    position: absolute;
    right: 4px;
    top: 4px;
    transition: all 100ms ease-out;
    z-index: -1;
  }

  .button-77:hover:not(:disabled):after {
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    transition-timing-function: ease-in;
  }

  .button-77:active:not(:disabled) {
    color: #ccc;
  }

  .button-77:active:not(:disabled):after {
    background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
      linear-gradient(92.83deg, #ff7426 0, #f93a13 100%);
    bottom: 4px;
    left: 4px;
    right: 4px;
    top: 4px;
  }

  .button-77:disabled {
    cursor: default;
    opacity: 0.24;
  }

  .main-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }

  .custom-toolbar {
    background: linear-gradient(to right, #a8edea, #e4d0ff);
  }
</style>